import React, { useEffect, useState } from "react";
import { stripeVerifySession } from "../../api/stripeVerifySession"; // new helper

export default function ThankyouPage() {
  const [statusMessage, setStatusMessage] = useState("Verifying payment...");
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id") || params.get("sessionId") || params.get("id");

    if (!sessionId) {
      // No session id — fallback to simple message and redirect after a short wait
      setStatusMessage("No session found. Redirecting to login...");
      setTimeout(() => {
        // replace with your login URL if needed
        window.location.href = "/";
      }, 3000);
      return;
    }

    const verify = async () => {
      try {
        setBusy(true);
        setStatusMessage("Verifying payment session with server...");

        // Use the helper which does a GET with correct params (sessionId + session_id)
        const data = await stripeVerifySession(sessionId);

        if (data?.success) {
          setStatusMessage("Payment confirmed. Redirecting to login...");
          // short delay so user can see confirmation
          setTimeout(() => {
            // replace with the desired login url (internal path or full host)
            window.location.href = "/";
          }, 1200);
        } else {
          setStatusMessage("Payment not confirmed yet. We'll keep trying...");
          // optional single retry after 2s using the same helper
          setTimeout(async () => {
            try {
              const r2 = await stripeVerifySession(sessionId);
              if (r2?.success) {
                window.location.href = "/";
              } else {
                setStatusMessage("Payment could not be verified. Please contact support.");
                setBusy(false);
              }
            } catch (err2) {
              console.error("Retry verification failed:", err2?.response ?? err2);
              setStatusMessage("Verification error. Please contact support.");
              setBusy(false);
            }
          }, 2000);
        }
      } catch (err) {
        // Show more details in console to help diagnose 404s / path issues
        console.error("Verification request failed:", {
          message: err.message,
          status: err?.response?.status,
          responseData: err?.response?.data,
          request: err?.request,
        });
        setStatusMessage(
          "Verification failed. Please contact support and include this page's URL (session id)."
        );
      } finally {
        setBusy(false);
      }
    };

    verify();
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-white p-6">
      <div className="w-full max-w-2xl text-center">
        <div className="flex justify-center mb-6">
          <div className="relative flex items-center justify-center w-32 h-32">
            <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-blue-500 animate-spin"></div>
            <div className="flex items-center justify-center w-28 h-28 rounded-full bg-blue-50 shadow-md">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#1462b0" className="w-16 h-16">
                <path d="M2 21h4V9H2v12zm20-11h-6.31l.95-4.57.03-.32a1 1 0 0 0-.29-.7L15.17 3 8.59 9.59C8.22 9.95 8 10.45 8 11v9a2 2 0 0 0 2 2h8c.83 0 1.54-.5 1.84-1.22l3.02-7.05c.09-.23.14-.47.14-.73V11c0-1.1-.9-2-2-2z" />
              </svg>
            </div>
          </div>
        </div>

        <h4 className="text-gray-600 font-semibold text-lg">Thank you!</h4>
        <h1 className="mt-4 text-3xl md:text-4xl font-light text-gray-700">You're almost there...</h1>
        <p className="mt-4 text-gray-500 text-lg">{statusMessage}</p>

        <div className="mt-8 flex items-center justify-center gap-4">
          <button
            className="px-6 py-2 rounded-full bg-white border border-gray-200 shadow-sm hover:shadow-md focus:outline-none"
            onClick={() => window.location.reload()}
            disabled={busy}
          >
            Refresh
          </button>
          <button
            className="px-6 py-2 rounded-full bg-[#1462b0] text-white font-medium shadow-md hover:bg-blue-800 focus:outline-none"
            onClick={() => (window.location.href = "/")}
          >
            Go to login
          </button>
        </div>

        <p className="mt-8 text-sm text-gray-400">If this takes longer than a minute, try refreshing the page or contact support.</p>
      </div>
    </div>
  );
}